import { dbGet, dbSet } from './db';
import { getCachedPlayBootstrap } from './cache';
import { enqueueRequest } from './queue';
import { isOnline } from './state';

const ATTEMPTS_KEY = 'assessment.attempts.v1';
const CURRENT_KEY = 'assessment.current.v1';

const SUBJECT_KEYS = {
  phonics: [
    'phonics_1_segmenting',
    'phonics_2_letter_naming',
    'phonics_3_blending',
    'phonics_4_word_reading',
  ],
  vocabulary: ['vocabulary_1', 'vocabulary_2', 'vocabulary_3'],
  reading: [
    'reading_1_fluency',
    'reading_1_comprehension',
    'reading_2_fluency',
    'reading_2_comprehension',
    'reading_3_fluency',
    'reading_3_comprehension',
  ],
  writing: ['writing_1'],
};

const READING_GROUPS = {
  reading_1: ['reading_1_fluency', 'reading_1_comprehension'],
  reading_2: ['reading_2_fluency', 'reading_2_comprehension'],
  reading_3: ['reading_3_fluency', 'reading_3_comprehension'],
};

const READING_LEVELS = {
  early_literacy: { reading_1: 5, reading_2: 7, reading_3: 10 },
  older_literacy: { reading_1: 2, reading_2: 6, reading_3: 10 },
};

const clone = (value) => JSON.parse(JSON.stringify(value));

async function getAttempts() { return (await dbGet(ATTEMPTS_KEY, {})) || {}; }
async function saveAttempts(attempts) { return dbSet(ATTEMPTS_KEY, attempts); }

function tempAttemptId() {
  return `offline-assessment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeStar(value) {
  if (value === 'green' || value === 'yellow') return value;
  if (value === true || value === 'true' || value === 1 || value === '1') return 'green';
  if (value === false || value === 'false' || value === 0 || value === '0') return 'yellow';
  return null;
}

function resolveSubject(attempt, activityKey) {
  const slot = (attempt?.assessmentActivitySlots || []).find((item) => item?.activityKey === activityKey);
  if (slot?.subject) return slot.subject;
  return Object.keys(SUBJECT_KEYS).find((subject) => SUBJECT_KEYS[subject].includes(activityKey)) || 'assessment';
}

function resolveAssessmentProfile(attempt = {}) {
  if (attempt.assessmentProfile === 'early_literacy' || attempt.assessmentProfile === 'older_literacy') {
    return attempt.assessmentProfile;
  }
  return attempt.ageBand === 'age_4_8' ? 'early_literacy' : 'older_literacy';
}

function resolveReadingLevelForGroup(attempt, groupKey) {
  const explicit = Number(attempt?.assessmentReadingAssignedLevels?.[groupKey]);
  if (Number.isFinite(explicit)) return explicit;
  if (groupKey === 'reading_3') return 10;
  const profile = resolveAssessmentProfile(attempt);
  return Number(READING_LEVELS[profile]?.[groupKey]) || null;
}

function resolveCorrectTotal(payload = {}) {
  const correctAnswers = Number(
    payload.correctAnswers
      ?? payload.problemsAnsweredCorrectly
      ?? payload.comprehensionCorrectAnswers
      ?? 0
  );
  const totalQuestions = Number(
    payload.totalQuestions
      ?? payload.allProblemsCount
      ?? payload.comprehensionTotalQuestions
      ?? 0
  );
  return {
    correctAnswers: Number.isFinite(correctAnswers) ? correctAnswers : 0,
    totalQuestions: Number.isFinite(totalQuestions) ? totalQuestions : 0,
  };
}

function evaluateLocalActivity(attempt, activityKey, payload = {}) {
  const { correctAnswers, totalQuestions } = resolveCorrectTotal(payload);
  const subject = resolveSubject(attempt, activityKey);
  let star = normalizeStar(payload.star)
    || normalizeStar(payload.isGreen)
    || normalizeStar(payload.isMastered);
  let assignedLevel = null;
  let meta = {};

  if (activityKey === 'phonics_1_segmenting') {
    star = correctAnswers >= 8 ? 'green' : 'yellow';
    assignedLevel = star === 'green' ? null : 1;
  } else if (activityKey === 'phonics_2_letter_naming') {
    star = correctAnswers >= 8 ? 'green' : 'yellow';
    assignedLevel = star === 'green' ? null : (correctAnswers >= 6 ? 3 : 2);
  } else if (activityKey === 'phonics_3_blending') {
    star = correctAnswers >= 8 ? 'green' : 'yellow';
    assignedLevel = star === 'green' ? null : 4;
  } else if (activityKey === 'phonics_4_word_reading') {
    const answersByQuestion = Array.isArray(payload.answersByQuestion)
      ? payload.answersByQuestion
      : Array.isArray(payload?.rawAssessment?.answersByQuestion)
        ? payload.rawAssessment.answersByQuestion
        : [];
    const answers = answersByQuestion.length
      ? answersByQuestion.map((value) => Number(Boolean(value)))
      : Array.from({ length: Math.max(0, totalQuestions) }, (_, index) => index < correctAnswers ? 1 : 0);
    const blockLevels = [5, 7, 8, 10];
    const failedBlocks = [];
    let start = 0;
    let blockIndex = 0;
    while (start < answers.length) {
      const remaining = answers.length - start;
      const size = blockIndex >= blockLevels.length - 1 ? remaining : Math.min(3, remaining);
      const block = answers.slice(start, start + size);
      const blockCorrect = block.reduce((sum, value) => sum + Number(value || 0), 0);
      if (blockCorrect < block.length) {
        failedBlocks.push({
          blockIndex,
          questionRange: `${start + 1}-${start + size}`,
          correctAnswers: blockCorrect,
          totalQuestions: block.length,
          assignedLevel: blockLevels[Math.min(blockIndex, blockLevels.length - 1)],
        });
      }
      start += size;
      blockIndex += 1;
    }
    assignedLevel = failedBlocks[0]?.assignedLevel ?? null;
    star = assignedLevel === null ? 'green' : 'yellow';
    meta = { failedBlocks, firstFailedBlockIndex: failedBlocks[0]?.blockIndex ?? -1 };
  } else if (activityKey === 'vocabulary_1' || activityKey === 'vocabulary_2' || activityKey === 'vocabulary_3') {
    const resolvedTotal = totalQuestions > 0 ? (totalQuestions <= 6 ? 6 : 10) : (resolveAssessmentProfile(attempt) === 'early_literacy' ? 6 : 10);
    const threshold = resolvedTotal <= 6 ? 5 : 8;
    const failedLevel = activityKey === 'vocabulary_1' ? 1 : activityKey === 'vocabulary_2' ? 5 : 8;
    star = correctAnswers >= threshold ? 'green' : 'yellow';
    assignedLevel = star === 'green' ? null : failedLevel;
    meta = { threshold, resolvedTotalQuestions: resolvedTotal };
  } else if (activityKey.startsWith('reading_')) {
    const groupKey = Object.keys(READING_GROUPS).find((key) => READING_GROUPS[key].includes(activityKey));
    const isComprehension = activityKey.endsWith('_comprehension');
    if (isComprehension && totalQuestions > 0) {
      star = correctAnswers >= Math.ceil(totalQuestions * 0.8) ? 'green' : 'yellow';
    }
    star = star || 'yellow';
    assignedLevel = star === 'yellow' ? resolveReadingLevelForGroup(attempt, groupKey) : null;
    meta = { groupKey, activityType: isComprehension ? 'comprehension' : 'fluency' };
  } else if (activityKey === 'writing_1') {
    star = star === 'yellow' ? 'yellow' : 'green';
  }

  return {
    activityKey,
    subject,
    star: star || null,
    assignedLevel,
    correctAnswers,
    totalQuestions,
    rawMetrics: payload.rawAssessment || payload.assessmentStats || payload,
    meta,
  };
}

function getCompletion(activityResults, subject) {
  const requiredKeys = SUBJECT_KEYS[subject] || [];
  const completedKeys = requiredKeys.filter((key) => activityResults.some((result) => result.activityKey === key && !!result.star));
  return {
    requiredKeys,
    completedKeys,
    isComplete: completedKeys.length === requiredKeys.length,
    completedCount: completedKeys.length,
    requiredCount: requiredKeys.length,
  };
}

function getFirstAssignedLevel(activityResults, subject) {
  return (SUBJECT_KEYS[subject] || [])
    .map((key) => activityResults.find((result) => result.activityKey === key))
    .find((result) => Number.isFinite(Number(result?.assignedLevel)))?.assignedLevel ?? null;
}

function normalizeStoredActivityResult(attempt, result = {}) {
  if (!result?.activityKey) return result;

  // Older offline attempts may have been persisted before the local evaluator
  // stored subject/assignedLevel. Re-evaluate from the original payload metrics
  // every time the local report is built so Reading/Writing do not depend on
  // reconnecting to the server first.
  const raw = result.rawMetrics && typeof result.rawMetrics === 'object'
    ? result.rawMetrics
    : {};
  const payload = {
    ...raw,
    star: result.star ?? raw.star,
    isGreen: raw.isGreen,
    isMastered: raw.isMastered,
    correctAnswers: result.correctAnswers ?? raw.correctAnswers,
    totalQuestions: result.totalQuestions ?? raw.totalQuestions,
    comprehensionCorrectAnswers: raw.comprehensionCorrectAnswers,
    comprehensionTotalQuestions: raw.comprehensionTotalQuestions,
  };

  const evaluated = evaluateLocalActivity(attempt, result.activityKey, payload);
  return {
    ...result,
    ...evaluated,
    // Keep the original captured payload intact for diagnostics/server parity.
    rawMetrics: result.rawMetrics || evaluated.rawMetrics,
  };
}

function buildLocalSummary(attempt) {
  const activityResults = (Array.isArray(attempt.activityResults) ? attempt.activityResults : [])
    .map((result) => normalizeStoredActivityResult(attempt, result));
  const completion = {
    phonics: getCompletion(activityResults, 'phonics'),
    vocabulary: getCompletion(activityResults, 'vocabulary'),
    reading: getCompletion(activityResults, 'reading'),
    writing: getCompletion(activityResults, 'writing'),
  };

  const phonicsAssigned = getFirstAssignedLevel(activityResults, 'phonics');
  const vocabularyAssigned = getFirstAssignedLevel(activityResults, 'vocabulary');

  const readingGroups = Object.keys(READING_GROUPS).map((groupKey) => {
    const requiredKeys = READING_GROUPS[groupKey];
    const items = requiredKeys.map((key) => activityResults.find((result) => result.activityKey === key)).filter(Boolean);
    const isComplete = items.length === requiredKeys.length;
    const allGreen = isComplete && items.every((item) => item.star === 'green');
    const hasGap = items.some((item) => item.star === 'yellow') || (isComplete && !allGreen);
    return {
      groupKey,
      requiredKeys,
      items,
      isComplete,
      star: allGreen ? 'green' : (hasGap ? 'yellow' : null),
      assignedLevel: hasGap ? resolveReadingLevelForGroup(attempt, groupKey) : null,
    };
  });
  const firstReadingAssigned = readingGroups.find((group) => Number.isFinite(Number(group.assignedLevel)))?.assignedLevel ?? null;

  let phonicsLevel = phonicsAssigned !== null ? Number(phonicsAssigned) : (completion.phonics.isComplete ? 11 : null);
  let readingLevel = firstReadingAssigned !== null
    ? Number(firstReadingAssigned)
    : (readingGroups.every((group) => group.isComplete && group.star === 'green') ? 11 : null);
  let vocabularyLevel = vocabularyAssigned !== null ? Number(vocabularyAssigned) : (completion.vocabulary.isComplete ? 11 : null);

  const language = String(attempt.instructionLanguage || attempt.language || attempt.explanationLanguage || '')
    .trim()
    .toLowerCase()
    .replace(/^english$/, 'en')
    .split(/[-_]/)[0];
  const placementConfigKey = String(attempt.placementConfigKey || '').trim().toLowerCase();
  if (placementConfigKey === 'adult:en' || (attempt.ageBand === 'adult' && language === 'en')) {
    vocabularyLevel = readingLevel;
  }
  const writingLevel = completion.writing.completedCount > 0 && readingLevel !== null ? readingLevel : null;

  const levels = {
    phonics: phonicsLevel,
    vocabulary: vocabularyLevel,
    reading: readingLevel,
    writing: writingLevel,
  };
  const expected = Array.isArray(attempt.expectedActivityKeys) ? attempt.expectedActivityKeys : [];
  const completed = new Set(activityResults.filter((item) => !!item.star).map((item) => item.activityKey).filter(Boolean));
  const pendingActivityKeys = expected.filter((key) => !completed.has(key));
  const assessmentComplete = Object.values(completion).every((item) => item.isComplete);
  const coreReady = ['phonics', 'vocabulary', 'reading'].every((key) => levels[key] !== null && levels[key] !== undefined);
  const report = {
    title: 'Customized Family Learning Report',
    titleStringNumber: 3709,
    familyMemberId: attempt.familyMemberId ? String(attempt.familyMemberId) : null,
    solutionId: attempt.solutionId ? String(attempt.solutionId) : null,
    ageBand: attempt.ageBand || null,
    assessmentProfile: attempt.assessmentProfile || resolveAssessmentProfile(attempt),
    language: attempt.language || attempt.instructionLanguage || attempt.explanationLanguage || null,
    generatedAt: new Date().toISOString(),
    levels,
    assessmentComplete,
    coreReady,
    progress: completion,
    offlinePendingServerEvaluation: true,
  };

  return {
    ...(attempt.summary || {}),
    status: coreReady ? 'finalized' : (attempt.status || 'in_progress'),
    mode: attempt.mode || 'mobile',
    outputType: 'custom_level',
    assessmentComplete,
    canProduceCustomLevel: coreReady,
    canProduceReport: coreReady,
    levels,
    completion,
    groupedReadingResults: readingGroups,
    pendingActivityKeys,
    completedActivityKeys: [...completed],
    isComplete: expected.length > 0 && pendingActivityKeys.length === 0,
    report,
    offlinePendingServerEvaluation: true,
  };
}

function buildLocalAssessmentMenu(attempt) {
  const slots = Array.isArray(attempt?.assessmentActivitySlots) && attempt.assessmentActivitySlots.length
    ? attempt.assessmentActivitySlots
    : (attempt?.expectedActivityKeys || []).map((activityKey, index) => ({
        activityKey,
        sourceActivityId: attempt?.assessmentLeafActivityIds?.[index] || null,
        order: index + 1,
        subject: String(activityKey || '').split('_')[0] || 'assessment',
        label: activityKey,
      }));
  const results = new Map((attempt?.activityResults || []).map((item) => [item.activityKey, item]));
  const bySubject = slots.reduce((acc, slot) => {
    const subject = slot?.subject || 'assessment';
    if (!acc[subject]) acc[subject] = [];
    acc[subject].push(slot);
    return acc;
  }, {});

  return slots.map((slot, index) => {
    const subject = slot?.subject || 'assessment';
    const sequence = bySubject[subject] || [];
    const sequenceIndex = sequence.findIndex((item) => item?.activityKey === slot?.activityKey);
    const previous = sequenceIndex > 0 ? sequence[sequenceIndex - 1] : null;
    const result = results.get(slot?.activityKey) || null;
    const previousDone = !previous || results.has(previous?.activityKey);
    return {
      activityKey: slot?.activityKey,
      sourceActivityId: result?.sourceActivityId || slot?.sourceActivityId || null,
      sourceActivityName: slot?.sourceActivityName || null,
      mappingSource: slot?.mappingSource || null,
      order: slot?.order || index + 1,
      subject,
      set: subject,
      sequenceIndex: sequenceIndex >= 0 ? sequenceIndex + 1 : index + 1,
      status: result ? 'completed' : (previousDone ? 'available' : 'locked'),
      star: result?.star || null,
      label: slot?.label || slot?.activityKey || '',
    };
  });
}

function refreshSummary(attempt) {
  const normalizedResults = (Array.isArray(attempt.activityResults) ? attempt.activityResults : [])
    .map((result) => normalizeStoredActivityResult(attempt, result));
  attempt.activityResults = normalizedResults;
  return buildLocalSummary(attempt);
}

export async function rememberAttempt(attempt) {
  if (!attempt?._id) return attempt;
  const attempts = await getAttempts();
  attempts[String(attempt._id)] = clone(attempt);
  await saveAttempts(attempts);
  await dbSet(CURRENT_KEY, { attemptId: String(attempt._id), familyMemberId: String(attempt.familyMemberId || ''), solutionId: String(attempt.solutionId || ''), mode: attempt.mode || 'mobile' });
  return attempt;
}

export async function getOfflineAttempt(attemptId) {
  const attempts = await getAttempts();
  return attempts[String(attemptId || '')] || null;
}

export async function getCurrentOfflineAttempt(params = {}) {
  const attempts = await getAttempts();
  const current = await dbGet(CURRENT_KEY, null);
  if (current?.attemptId && attempts[current.attemptId]) {
    const attempt = attempts[current.attemptId];
    if (
      (!params.familyMemberId || String(attempt.familyMemberId || '') === String(params.familyMemberId))
      && (!params.solutionId || String(attempt.solutionId || '') === String(params.solutionId))
      && (!params.mode || String(attempt.mode || '') === String(params.mode))
    ) return attempt;
  }

  return Object.values(attempts).reverse().find((attempt) => (
    (!params.familyMemberId || String(attempt.familyMemberId || '') === String(params.familyMemberId))
    && (!params.solutionId || String(attempt.solutionId || '') === String(params.solutionId))
    && (!params.mode || String(attempt.mode || '') === String(params.mode))
  )) || null;
}

export async function createOfflineAttempt(data = {}) {
  const existing = await getCurrentOfflineAttempt(data);
  if (existing && !data.forceNewAttempt) return existing;

  const playData = await getCachedPlayBootstrap();
  const blueprint = playData?.mobileBootstrap?.offlineAssessment || {};
  const expectedActivityKeys = Array.isArray(data.activityPlan) && data.activityPlan.length
    ? data.activityPlan
    : (Array.isArray(blueprint.expectedActivityKeys) ? blueprint.expectedActivityKeys : []);
  const assessmentActivitySlots = Array.isArray(blueprint.assessmentActivitySlots)
    ? blueprint.assessmentActivitySlots
    : [];

  if ((data.mode || 'mobile') === 'mobile' && !expectedActivityKeys.length) {
    throw new Error('Placement is not prepared for offline use yet. Connect to the internet once to download Placement content.');
  }

  const attempt = {
    _id: tempAttemptId(),
    familyMemberId: data.familyMemberId || null,
    solutionId: data.solutionId || playData?.mobileBootstrap?.solutionId || null,
    mode: data.mode || 'mobile',
    ageBand: data.ageBand || blueprint.ageBand || null,
    assessmentProfile: data.assessmentProfile || blueprint.assessmentProfile || null,
    assessmentReadingAssignedLevels: data.assessmentReadingAssignedLevels || blueprint.assessmentReadingAssignedLevels || null,
    explanationLanguage: data.explanationLanguage || null,
    instructionLanguage: data.instructionLanguage || data.language || blueprint.instructionLanguage || null,
    language: data.language || data.instructionLanguage || data.explanationLanguage || blueprint.instructionLanguage || null,
    assessmentRootGroupId: data.assessmentRootGroupId || blueprint.assessmentRootGroupId || null,
    assessmentLeafActivityIds: Array.isArray(blueprint.assessmentLeafActivityIds) ? blueprint.assessmentLeafActivityIds : [],
    assessmentActivitySlots,
    expectedActivityKeys,
    placementConfigKey: blueprint.placementConfigKey || null,
    placementMenuGroupName: blueprint.placementMenuGroupName || null,
    activityResults: [],
    status: 'in_progress',
    startedAt: new Date().toISOString(),
    offlineLocalAttempt: true,
  };
  attempt.summary = refreshSummary(attempt);
  attempt.reportSnapshot = { report: attempt.summary.report, offline: true };
  attempt.assessmentMenu = buildLocalAssessmentMenu(attempt);
  await rememberAttempt(attempt);
  await enqueueRequest({
    id: `assessment-start:${attempt._id}`,
    method: 'post',
    url: '/api/mobile-v2/assessment/start',
    data: { ...data, offlineClientAttemptId: attempt._id },
    kind: 'assessment-start',
    localAttemptId: attempt._id,
  });
  return attempt;
}

export async function submitOfflineActivity(attemptId, data = {}) {
  const attempts = await getAttempts();
  const attempt = attempts[String(attemptId)] || await getCurrentOfflineAttempt({});
  if (!attempt) throw new Error('Offline assessment attempt was not prepared before the connection was lost.');

  const activityKey = data.activityKey;
  const payload = data.payload || {};
  const evaluated = evaluateLocalActivity(attempt, activityKey, payload);
  const result = {
    ...evaluated,
    sourceActivityId: data.sourceActivityId || payload.sourceActivityId || null,
    sourceActivityGroupId: data.sourceActivityGroupId || payload.sourceActivityGroupId || null,
    submittedAt: new Date().toISOString(),
    offlinePendingServerEvaluation: true,
  };

  const existingIndex = (attempt.activityResults || []).findIndex((item) => item.activityKey === activityKey);
  if (existingIndex >= 0) attempt.activityResults[existingIndex] = result;
  else attempt.activityResults = [...(attempt.activityResults || []), result];
  attempt.summary = refreshSummary(attempt);
  attempt.reportSnapshot = { report: attempt.summary.report, offline: true };
  attempt.assessmentMenu = buildLocalAssessmentMenu(attempt);
  attempts[String(attempt._id)] = attempt;
  await saveAttempts(attempts);

  await enqueueRequest({
    method: 'post',
    url: `/api/mobile-v2/assessment/${attempt._id}/activities`,
    data,
    kind: 'assessment-activity',
    localAttemptId: attempt._id,
  });

  return { attempt: clone(attempt), submittedResult: clone(result), offline: true };
}

export async function finalizeOfflineAttempt(attemptId, data = {}) {
  const attempts = await getAttempts();
  const attempt = attempts[String(attemptId)] || await getCurrentOfflineAttempt({});
  if (!attempt) throw new Error('Offline assessment attempt was not prepared before the connection was lost.');
  attempt.summary = refreshSummary(attempt);
  attempt.reportSnapshot = { report: attempt.summary.report, offline: true };
  attempt.assessmentMenu = buildLocalAssessmentMenu(attempt);
  attempt.status = attempt.summary?.isComplete ? 'offline_pending_sync' : 'in_progress';
  attempt.offlinePendingFinalize = true;
  attempts[String(attempt._id)] = attempt;
  await saveAttempts(attempts);
  await enqueueRequest({
    method: 'post',
    url: `/api/mobile-v2/assessment/${attempt._id}/finalize`,
    data,
    kind: 'assessment-finalize',
    localAttemptId: attempt._id,
  });
  return {
    attempt: clone(attempt),
    customLevelRecord: null,
    reportSnapshot: clone(attempt.reportSnapshot),
    offline: true,
    pendingSync: true,
  };
}

export async function offlineAssessmentReport(attemptId) {
  const attempt = await getOfflineAttempt(attemptId);
  if (!attempt) return null;
  const refreshed = clone(attempt);
  refreshed.summary = buildLocalSummary(refreshed);
  refreshed.reportSnapshot = { report: refreshed.summary.report, offline: true };
  return {
    attempt: refreshed,
    reportSnapshot: clone(refreshed.reportSnapshot),
    offline: true,
  };
}

export async function prepareAssessmentCache(api, playData) {
  if (!isOnline() || !playData?.learner?._id || !playData?.mobileBootstrap?.solutionId) return;
  const generated = playData?.mobileBootstrap?.generatedCustomContentLevel;
  if (generated?._id) return;

  try {
    let current = await api.getCurrentAttempt({
      familyMemberId: playData.learner._id,
      solutionId: playData.mobileBootstrap.solutionId,
      mode: 'mobile',
    });
    if (current?.attempt) current = current.attempt;
    if (current?._id) await rememberAttempt(current);
  } catch (_) {
    // The normal UI will create the attempt. Once created, the API wrapper stores it.
  }
}
